import { AxiosError, AxiosRequestConfig } from 'axios';
import { useLayoutEffect, useRef } from 'react';
import { refreshToken } from 'api/authenticate';
import client from 'api/client';
import { httpErrorNotification } from 'api/httpErrorNotification';
import setAuthorizationToken from 'api/setAuthorizationToken';
import useAuth from 'hooks/useAuth';
import { UserProfilesNames } from 'src/types/claims';
import blobStorageClient from 'src/api/blobStorageClient';
import goClient from 'src/api/goClient';
import mailingClient from 'src/api/mailingClient';
import userClient from 'src/api/userClient';

interface RetryConfig extends AxiosRequestConfig {
  retry?: boolean;
}
export const AxiosConfigurator: React.FC = () => {
  const { user, logout } = useAuth();
  const isRefreshingRef = useRef<boolean>(false);

  let e401resolved = true;
  useLayoutEffect(() => {
    const handle401 = async (err: AxiosError, axiosInstance: typeof client) => {
      const config: RetryConfig = err.config ?? { retry: false };
      const requestData = config.data?.toString() || '';
      if (requestData.includes('grant_type=password')) {
        return Promise.reject(err);
      }
      if (err.response?.status === 401 && e401resolved) {
        if (client.defaults.headers.common.Authorization && !config.retry) {
          isRefreshingRef.current = true;
          config.retry = true;
          try {
            const response = await refreshToken();
            if (response && config.headers) {
              setAuthorizationToken();
              config.headers.Authorization =
                axiosInstance.defaults.headers.common.Authorization;
              isRefreshingRef.current = false;
              return axiosInstance(config);
            }
          } catch (refreshError) {
            isRefreshingRef.current = false;
            logout();
            return Promise.reject(refreshError);
          }
        }

        if (err.response?.status === 401) {
          if (user?.profileName === UserProfilesNames.Inspector) {
            window.location.replace('/');
            return Promise.reject();
          } else {
            logout();
            return Promise.reject();
          }
        }
      }
      httpErrorNotification(err);
      return Promise.reject(err);
    };

    const responseIntercept = client.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, client)
    );
    const blobStorageIntercept = blobStorageClient.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, blobStorageClient)
    );
    const goIntercept = goClient.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, goClient)
    );
    const mailingIntercept = mailingClient.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, mailingClient)
    );
    const userIntercept = userClient.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handle401(err, userClient)
    );

    return () => {
      client.interceptors.response.eject(responseIntercept);
      blobStorageClient.interceptors.response.eject(blobStorageIntercept);
      goClient.interceptors.response.eject(goIntercept);
      mailingClient.interceptors.response.eject(mailingIntercept);
      userClient.interceptors.response.eject(userIntercept);
    };
  }, [logout, user]);
  return null;
};
